import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft, Linkedin } from 'lucide-react';
import BlogLayout from './BlogLayout';
import SignatureSVG from './SignatureSVG';
import { useSeo } from '../hooks/useSeo';
import { useReveal } from '../hooks/useScrollFx';
import { useBlogLang } from '../hooks/useBlogLang';
import { IMAGES } from '../data/lempoleData';

const SITE = 'https://lempole.com';
const LINKEDIN_URL = 'https://it.linkedin.com/in/raffaello-tiezzi-ab566b13';

// Bilingual copy for the whole page. Kept in the component so translations
// stay next to their layout — no separate data file for a single page.
// Exported so the same story can be reused inside `VigneronModal` (the popup
// version rendered from the home page bridge section).
export const COPY = {
  it: {
    backToSite: 'Torna al sito',
    eyebrow: 'il vigneron',
    h1a: 'Raffaello',
    h1b: 'Tiezzi.',
    lead: 'Sono indipendente per vocazione. Un tecnico esperto con l\u2019anima dell\u2019artigiano, tornato a Lamole per restituire dignità a un paesaggio dimenticato.',
    linkedin: 'Profilo LinkedIn',
    tagPhoto: 'Raffaello, tra i filari',
    portraitAlt: 'Raffaello Tiezzi nel vigneto di Lamole',
    ch1Title: ['Le prime radici in', 'Toscana'],
    ch1: [
      'Ho imparato a leggere la lingua delle viti nel cuore della Toscana centrale. Sono cresciuto accanto ai filari, con il vocabolario lento di chi passa più tempo a osservare che a parlare. La mia formazione arriva a Firenze, dove mi laureo in Viticoltura ed Enologia — ma il vero apprendistato, per me, è cominciato molto prima, in vigna.',
      'È lì che è nata la mia convinzione più profonda: la natura non accetta scorciatoie. Un\u2019idea che oggi guida ogni gesto di Lempole e di Madonnino dei Campacci.',
    ],
    quote1: '“Qui, l\u2019agricoltura non è fretta, ma ascolto.”',
    ch2Title: ['Vent\u2019anni di vigne', 'per il mondo'],
    ch2: [
      'La curiosità mi ha portato lontano. Prima la Nuova Zelanda, dove ho vissuto l\u2019intensità di una vendemmia agli antipodi. Poi le esperienze in Frescobaldi. Con Bibi Graetz e Poggio al Sole ho affinato una sensibilità agronomica che oggi riconosci in ogni scelta di Lempole. Un capitolo fondamentale è stato il tempo con il Gruppo Casadei — nelle tenute del gruppo, in Autoctona (anch\u2019essa parte del gruppo) e in progetti speciali all\u2019estero, come un\u2019esperienza in Montenegro: è l\u2019apprendistato che mi ha dato una visione tecnica del vino sempre attenta al territorio. Ancora oggi collaboro con Spevis e sono consulente per piccole e selezionate realtà italiane, tenendo un piede saldo nella consulenza agronomica ed enologica mentre coltivo, senza fretta, il mio progetto personale a Lamole. In mezzo, molti viaggi di studio e condivisione in Francia — in Borgogna e in Champagne — dove ho imparato ad ascoltare come la finezza sappia nascere dalla pazienza.',
      'In vent\u2019anni ho raccolto ogni ruolo possibile — operaio, trattorista, cantiniere, agronomo, consulente — perché ogni gesto, per me, è un tassello in più del mestiere. È il curriculum di chi ha scelto la fatica del campo alla comodità della teoria.',
    ],
    ch3Title: ['Il ritorno a', 'Lamole'],
    ch3: [
      'Nel 2024 ho deciso di riportare in vita un pezzo di paesaggio abbandonato da oltre sessant\u2019anni: i terrazzamenti in pietra a secco del Madonnino dei Campacci, a Lamole, 600 metri di quota sul cuore antico del Chianti Classico. È un\u2019operazione di rinascita silenziosa più che di business: rialzare muretti, liberare fasce dal bosco, ridare fiato a viti dimenticate.',
      'Accanto ai terrazzamenti, un piccolo oliveto sotto lo stesso cielo di Lamole: un mosaico di gesti tenuti insieme dalla stessa idea di piccole produzioni e cura totale.',
    ],
    quote2: '“Voglio restituire dignità agricola a questi terrazzamenti — nel cuore pulsante e antico del Chianti Classico.”',
    ch4Title: ['Un bradipo', 'come manifesto'],
    ch4a: 'La prima etichetta di questo cammino è Lempole: un Sangiovese Rosato IGT Toscana, 12,5% vol., vinificato esclusivamente in acciaio per proteggere la freschezza, prodotto in sole 1.000 bottiglie numerate. Sull\u2019etichetta, il bradipo. Non è un ornamento: è un elogio alla lentezza necessaria per raggiungere la vera qualità.',
    ch4bPrefix: 'Il progetto continuerà con calma, come tutto ciò che riguarda Raffaello. Un rosso Sangiovese, un metodo classico, un olio EVO, un miele — nella promessa che accompagna ogni bottiglia: ',
    ch4bItalic: 'poche bottiglie, molta anima, nessuna fretta.',
    ctaTitle: ['Continua il', 'cammino'],
    ctaReturn: 'Il ritorno del giovane vigneron',
    ctaDiario: 'Tutto il diario di Lempole',
    seoTitle: 'Chi è Raffaello Tiezzi — Il vigneron di Lempole a Lamole',
    seoDesc: 'La storia di Raffaello Tiezzi: dagli studi di Viticoltura ed Enologia a Firenze alle vendemmie in Nuova Zelanda, da Frescobaldi a Bibi Graetz e Poggio al Sole, dal Gruppo Casadei ad Autoctona, con viaggi di studio in Francia tra Borgogna e Champagne e la collaborazione tutt\u2019oggi in Spevis, fino al ritorno a Lamole per fondare Madonnino dei Campacci e Lempole.',
    seoKeywords: 'Raffaello Tiezzi, vigneron Lamole, Madonnino dei Campacci, Lempole, viticoltura eroica, Chianti Classico',
  },
  en: {
    backToSite: 'Back to site',
    eyebrow: 'the vigneron',
    h1a: 'Raffaello',
    h1b: 'Tiezzi.',
    lead: 'Independent by vocation. An experienced technician with the soul of a craftsman, I\u2019m back in Lamole to give dignity again to a forgotten landscape.',
    linkedin: 'LinkedIn profile',
    tagPhoto: 'Raffaello, among the rows',
    portraitAlt: 'Raffaello Tiezzi in the Lamole vineyard',
    ch1Title: ['First roots in', 'Tuscany'],
    ch1: [
      'I learned to read the language of vines in the heart of central Tuscany. I grew up next to the rows, with the slow vocabulary of someone who spends more time observing than speaking. My formal education happened in Florence, where I graduated in Viticulture and Oenology — but the real apprenticeship, for me, began much earlier, in the vineyard.',
      'That\u2019s where my deepest belief took shape: nature does not accept shortcuts. An idea that guides every gesture of Lempole and Madonnino dei Campacci today.',
    ],
    quote1: '“Here, farming is not haste — it\u2019s listening.”',
    ch2Title: ['Twenty years of vineyards', 'across the world'],
    ch2: [
      'Curiosity took me far. First New Zealand, where I experienced the intensity of an antipodean harvest. Then my experiences with Frescobaldi. With Bibi Graetz and Poggio al Sole I refined an agronomic sensibility you can recognise in every choice Lempole makes. A pivotal chapter was my time with the Casadei Group — across the group\u2019s estates, at Autoctona (also part of the group), and on special projects abroad, including an experience in Montenegro: the apprenticeship that gave me a technical vision of wine always rooted in terroir. I still collaborate today with Spevis, and I consult for small, hand-picked Italian estates — keeping one foot firmly in agronomic and oenological consulting while I grow, unhurried, my own project in Lamole. Along the way, many study trips and exchanges in France — in Burgundy and in Champagne — where I learned to listen to how finesse is born of patience.',
      'In twenty years I took on every possible role — worker, tractor driver, cellarhand, agronomist, consultant — because for me every gesture is another tile in the craft. It\u2019s the résumé of someone who chose the toil of the field over the comfort of theory.',
    ],
    ch3Title: ['The return to', 'Lamole'],
    ch3: [
      'In 2024 I decided to bring back to life a piece of landscape abandoned for over sixty years: the dry-stone terraces of Madonnino dei Campacci, in Lamole, 600 metres above the ancient heart of Chianti Classico. It\u2019s a quiet revival more than a business move: raise the walls, clear strips from the forest, give breath to forgotten vines.',
      'Alongside the terraces, a small olive grove under the same Lamole sky: a mosaic of gestures held together by the same idea of tiny production and total care.',
    ],
    quote2: '“I want to give farming dignity back to these terraces — in the pulsing, ancient heart of Chianti Classico.”',
    ch4Title: ['A sloth', 'as manifesto'],
    ch4a: 'The first label on this journey is Lempole: a Sangiovese Rosato IGT Toscana, 12.5% ABV, vinified exclusively in stainless steel to protect freshness, produced in just 1,000 numbered bottles. On the label, the sloth. It\u2019s not ornament: it\u2019s a tribute to the slowness true quality demands.',
    ch4bPrefix: 'The project will keep unfolding with calm, as everything about Raffaello does. A Sangiovese red, a metodo classico, an EVO oil, a honey — under the promise every bottle carries: ',
    ch4bItalic: 'few bottles, plenty of soul, no rush.',
    ctaTitle: ['Keep', 'walking'],
    ctaReturn: 'The return of the young vigneron',
    ctaDiario: 'The full Lempole diary',
    seoTitle: 'Who is Raffaello Tiezzi — The vigneron of Lempole in Lamole',
    seoDesc: 'The story of Raffaello Tiezzi: from studying Viticulture and Oenology in Florence to the harvest in New Zealand, from Frescobaldi to Bibi Graetz and Poggio al Sole, from the Casadei Group to Autoctona, with study trips in France between Burgundy and Champagne and an ongoing collaboration with Spevis, to the return to Lamole to found Madonnino dei Campacci and Lempole.',
    seoKeywords: 'Raffaello Tiezzi, Lamole vigneron, Madonnino dei Campacci, Lempole, heroic viticulture, Chianti Classico',
  },
  fr: {
    backToSite: 'Retour au site',
    eyebrow: 'le vigneron',
    h1a: 'Raffaello',
    h1b: 'Tiezzi.',
    lead: 'Indépendant par vocation. Un technicien expérimenté à l\u2019âme d\u2019artisan, je suis revenu à Lamole pour rendre sa dignité à un paysage oublié.',
    linkedin: 'Profil LinkedIn',
    tagPhoto: 'Raffaello, entre les rangs',
    portraitAlt: 'Raffaello Tiezzi dans le vignoble de Lamole',
    ch1Title: ['Les premières racines en', 'Toscane'],
    ch1: [
      'J\u2019ai appris à lire la langue des vignes au cœur de la Toscane centrale. J\u2019ai grandi à côté des rangs, avec le vocabulaire lent de celui qui passe plus de temps à observer qu\u2019à parler. Ma formation s\u2019est faite à Florence, où j\u2019ai obtenu un diplôme en Viticulture et Œnologie — mais le vrai apprentissage, pour moi, a commencé bien avant, à la vigne.',
      'C\u2019est là qu\u2019est née ma conviction la plus profonde : la nature n\u2019accepte pas les raccourcis. Une idée qui guide aujourd\u2019hui chaque geste de Lempole et de Madonnino dei Campacci.',
    ],
    quote1: '« Ici, l\u2019agriculture n\u2019est pas de la hâte, c\u2019est de l\u2019écoute. »',
    ch2Title: ['Vingt ans de vignes', 'à travers le monde'],
    ch2: [
      'La curiosité m\u2019a emmené loin. D\u2019abord la Nouvelle-Zélande, où j\u2019ai vécu l\u2019intensité d\u2019une vendange aux antipodes. Puis les expériences chez Frescobaldi. Avec Bibi Graetz et Poggio al Sole j\u2019ai affiné une sensibilité agronomique que l\u2019on reconnaît aujourd\u2019hui dans chaque choix de Lempole. Un chapitre fondamental a été mon temps avec le Groupe Casadei — dans les domaines du groupe, chez Autoctona (également partie du groupe) et sur des projets spéciaux à l\u2019étranger, dont une expérience au Monténégro : c\u2019est l\u2019apprentissage qui m\u2019a donné une vision technique du vin toujours attentive au territoire. Je collabore encore aujourd\u2019hui avec Spevis et je suis consultant pour de petites structures italiennes soigneusement choisies, gardant un pied solidement ancré dans le conseil agronomique et œnologique tout en cultivant, sans hâte, mon projet personnel à Lamole. En parallèle, de nombreux voyages d\u2019étude et de partage en France — en Bourgogne et en Champagne — où j\u2019ai appris à écouter comment la finesse naît de la patience.',
      'En vingt ans, j\u2019ai endossé tous les rôles possibles — ouvrier, tractoriste, caviste, agronome, consultant — parce que pour moi chaque geste est un maillon de plus du métier. C\u2019est le CV de celui qui a préféré la peine du champ au confort de la théorie.',
    ],
    ch3Title: ['Le retour à', 'Lamole'],
    ch3: [
      'En 2024, j\u2019ai décidé de ramener à la vie un morceau de paysage abandonné depuis plus de soixante ans : les terrasses en pierre sèche du Madonnino dei Campacci, à Lamole, 600 mètres d\u2019altitude au-dessus du cœur ancien du Chianti Classico. C\u2019est plus une opération de renaissance silencieuse qu\u2019une affaire : relever les murs, libérer les bandes de la forêt, redonner du souffle à des vignes oubliées.',
      'À côté des terrasses, une petite oliveraie sous le même ciel de Lamole : une mosaïque de gestes tenue ensemble par la même idée de petites productions et de soin total.',
    ],
    quote2: '« Je veux rendre leur dignité agricole à ces terrasses — au cœur battant et ancien du Chianti Classico. »',
    ch4Title: ['Un paresseux', 'comme manifeste'],
    ch4a: 'La première étiquette de ce cheminement est Lempole : un Sangiovese Rosato IGT Toscana, 12,5 % vol., vinifié exclusivement en inox pour protéger la fraîcheur, produit en seulement 1 000 bouteilles numérotées. Sur l\u2019étiquette, le paresseux. Ce n\u2019est pas un ornement : c\u2019est un éloge de la lenteur nécessaire pour atteindre la vraie qualité.',
    ch4bPrefix: 'Le projet continuera avec calme, comme tout ce qui concerne Raffaello. Un rouge Sangiovese, un méthode classique, une huile d\u2019olive extra vierge, un miel — sous la promesse qui accompagne chaque bouteille : ',
    ch4bItalic: 'peu de bouteilles, beaucoup d\u2019âme, aucune hâte.',
    ctaTitle: ['Continuez le', 'chemin'],
    ctaReturn: 'Le retour du jeune vigneron',
    ctaDiario: 'Tout le journal de Lempole',
    seoTitle: 'Qui est Raffaello Tiezzi — Le vigneron de Lempole à Lamole',
    seoDesc: 'L\u2019histoire de Raffaello Tiezzi : des études de Viticulture et Œnologie à Florence à la vendange en Nouvelle-Zélande, de Frescobaldi à Bibi Graetz et Poggio al Sole, du Groupe Casadei à Autoctona, avec des voyages d\u2019étude en France entre Bourgogne et Champagne et une collaboration toujours en cours avec Spevis, jusqu\u2019au retour à Lamole pour fonder Madonnino dei Campacci et Lempole.',
    seoKeywords: 'Raffaello Tiezzi, vigneron Lamole, Madonnino dei Campacci, Lempole, viticulture héroïque, Chianti Classico',
  },
};

export default function VigneronPage() {
  const { lang } = useBlogLang();
  const c = COPY[lang] || COPY.it;
  const rootRef = useReveal();

  const suffix = lang === 'it' ? '' : `?lang=${lang}`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${SITE}/vigneron`,
    inLanguage: lang === 'en' ? 'en-US' : (lang === 'fr' ? 'fr-FR' : 'it-IT'),
    url: `${SITE}/vigneron${suffix}`,
    mainEntity: {
      '@type': 'Person',
      '@id': `${SITE}/#raffaello`,
      name: 'Raffaello Tiezzi',
      jobTitle: 'Vigneron',
      description:
        'Fondatore di Madonnino dei Campacci e Lempole. Laurea in Viticoltura ed Enologia a Firenze, esperienze in Nuova Zelanda, Frescobaldi, Bibi Graetz, Poggio al Sole, Gruppo Casadei, Autoctona, viaggi di studio in Francia tra Borgogna e Champagne, e collaborazione tutt’oggi con Spevis. Oggi restituisce dignità agricola ai terrazzamenti storici di Lamole.',
      knowsAbout: [
        'Viticoltura eroica',
        'Slow Wine',
        'Sangiovese',
        'Terrazzamenti Lamole',
        'Chianti Classico',
        'Enologia',
      ],
      alumniOf: {
        '@type': 'EducationalOrganization',
        name: 'Università di Firenze — Viticoltura ed Enologia',
      },
      worksFor: { '@id': `${SITE}/#winery` },
      sameAs: [LINKEDIN_URL, 'https://instagram.com/lempole.wine'],
      image: IMAGES.vigneron,
    },
  }), [lang, suffix]);

  useSeo({
    title: c.seoTitle,
    description: c.seoDesc,
    canonical: `${SITE}/vigneron${suffix}`,
    image: IMAGES.vigneron,
    type: 'profile',
    keywords: c.seoKeywords,
    jsonLd,
  });

  return (
    <BlogLayout>
      <div ref={rootRef} className="vigneron-page">
        {/* Breadcrumb + eyebrow header */}
        <header className="vigneron-head">
          <Link to={`/${suffix}`} className="vigneron-crumb font-kicker" data-testid="vigneron-back">
            <ArrowLeft size={14} /> {c.backToSite}
          </Link>
        </header>

        {/* HERO — text-only (portrait intentionally omitted per Raffaello's request) */}
        <section className="vigneron-hero vigneron-hero--text-only">
          <div className="vigneron-hero__meta reveal">
            <span className="eyebrow">{c.eyebrow}</span>
            <h1 className="vigneron-hero__title font-display">
              {c.h1a} <em className="font-display-italic">{c.h1b}</em>
            </h1>
            <p className="vigneron-hero__lead font-display-italic">{c.lead}</p>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noreferrer"
              className="vigneron-linkedin"
              aria-label="Raffaello Tiezzi su LinkedIn"
              data-testid="vigneron-linkedin"
            >
              <Linkedin size={16} />
              <span>{c.linkedin}</span>
            </a>
          </div>
        </section>

        {/* BODY — chapters */}
        <article className="vigneron-body">
          <section className="vigneron-chapter reveal">
            <span className="vigneron-chapter__num font-caveat">01</span>
            <h2 className="vigneron-chapter__title font-display">
              {c.ch1Title[0]} <em className="font-display-italic">{c.ch1Title[1]}</em>
            </h2>
            {c.ch1.map((p, i) => (<p key={i}>{p}</p>))}
          </section>

          <blockquote className="vigneron-quote reveal font-display-italic">
            {c.quote1}
          </blockquote>

          <section className="vigneron-chapter reveal">
            <span className="vigneron-chapter__num font-caveat">02</span>
            <h2 className="vigneron-chapter__title font-display">
              {c.ch2Title[0]} <em className="font-display-italic">{c.ch2Title[1]}</em>
            </h2>
            {c.ch2.map((p, i) => (<p key={i}>{p}</p>))}
          </section>

          <section className="vigneron-chapter reveal">
            <span className="vigneron-chapter__num font-caveat">03</span>
            <h2 className="vigneron-chapter__title font-display">
              {c.ch3Title[0]} <em className="font-display-italic">{c.ch3Title[1]}</em>
            </h2>
            {c.ch3.map((p, i) => (<p key={i}>{p}</p>))}
          </section>

          <blockquote className="vigneron-quote reveal font-display-italic">
            {c.quote2}
          </blockquote>

          <section className="vigneron-chapter reveal">
            <span className="vigneron-chapter__num font-caveat">04</span>
            <h2 className="vigneron-chapter__title font-display">
              {c.ch4Title[0]} <em className="font-display-italic">{c.ch4Title[1]}</em>
            </h2>
            <p>{c.ch4a}</p>
            <p>
              {c.ch4bPrefix}
              <em className="font-display-italic">{c.ch4bItalic}</em>
            </p>
          </section>

          <div className="vigneron-sign reveal">
            <SignatureSVG
              style={{
                width: 'min(260px, 60vw)',
                height: 'auto',
                color: '#3a2418',
                display: 'block',
              }}
            />
          </div>
        </article>

        {/* CTAs */}
        <section className="vigneron-ctas reveal">
          <h2 className="vigneron-ctas__title font-display">
            {c.ctaTitle[0]} <em className="font-display-italic">{c.ctaTitle[1]}</em>
          </h2>
          <div className="vigneron-ctas__links">
            <Link
              to={`/diario/ritorno-vigneron-lamole${suffix}`}
              className="hero-cta hero-cta--ink inline-flex items-center gap-3"
              data-testid="vigneron-cta-return"
            >
              <span className="hero-cta__line" aria-hidden="true" />
              <span className="hero-cta__text font-display-italic">
                {c.ctaReturn}
              </span>
              <ChevronRight size={18} className="hero-cta__arrow" />
            </Link>
            <Link
              to={`/diario${suffix}`}
              className="hero-cta hero-cta--ink inline-flex items-center gap-3"
              data-testid="vigneron-cta-diario"
            >
              <span className="hero-cta__line" aria-hidden="true" />
              <span className="hero-cta__text font-display-italic">
                {c.ctaDiario}
              </span>
              <ChevronRight size={18} className="hero-cta__arrow" />
            </Link>
          </div>
        </section>
      </div>
    </BlogLayout>
  );
}
